<template>
	<div class="measurements-selector__meta-information">
		<Teleport to="#measurements-selector-pricing-table">
			<div
				v-if="isShapeComplete || shouldShowSpinner"
				class="measurements-selector__product-price-total"
			>
				<div class="measurements-selector__product-price measurements-selector__product-price--large">
					<i v-if="shouldShowSpinner" class="icon icon-spinner2"></i>
					<FormattedPrice v-else :price="totalPriceIncVat" dataTestID="product_action_bar_price_incl" />
				</div>
				<div class="measurements-selector__price measurements-selector__product-price--small">
					<i v-if="shouldShowSpinner" class="icon icon-spinner2"></i>
					<FormattedPrice v-else :price="totalPriceExVat" dataTestID="product_action_bar_price_excl" /> {{ i18n.vatExcluded }}
				</div>
			</div>
		</Teleport>
		<div class="measurements-selector__price-structure">
			<ul class="measurements-selector__price-structure-nav">
				<li class="measurements-selector__price-structure-nav-item" :class="{ 'measurements-selector__price-structure-nav-item--active': showPriceStructure }">
					<button
						v-if="isShapeComplete"
						class="btn btn--extra-small btn--clean-link btn--clean-icon"
						type="button"
						@click="showPriceStructure = ! showPriceStructure; addDataLayerEvent()">
						<span class="btn__icon">
							<ChevronRightIcon class="icon-inline icon-inline--expanded svg-icon-chevron-right-icon" aria-hidden="true" />
						</span>
						<span class="btn__text">{{ showPriceStructure ? i18n.meta.hidePriceStructure : i18n.meta.showPriceStructure }}</span>
					</button>
				</li>
				<li class="measurements-selector__price-structure-nav-item" :class="{ 'measurements-selector__price-structure-nav-item--faded': showPriceStructure }">
					<div class="measurements-selector__save-and-share">
						<StoreButton />
						<StoreModal />
					</div>
				</li>
				<li class="measurements-selector__price-structure-nav-item" :class="{ 'measurements-selector__price-structure-nav-item--faded': showPriceStructure }">
					<div class="measurements-selector__save-and-share">
						<ShareButton />
						<ShareModal />
					</div>
				</li>
			</ul>
			<measurements-height-transition appear mode="out-in">
				<div class="measurements-selector__pricing" :aria-hidden="!showPriceStructure" v-show="showPriceStructure">
					<table class="measurements-selector__pricing-table"  v-if="isShapeComplete || shouldShowSpinner">
						<tbody>
							<tr>
								<td>{{ i18n.meta.materialPrice }}</td>
								<PriceStructureComponent :price="pricesIncVat.material" :isEx="false" :showSpinner="shouldShowSpinner" />
								<PriceStructureComponent :price="pricesExVat.material" :isEx="true" :showSpinner="shouldShowSpinner" />
							</tr>
							<tr>
								<td>{{ i18n.meta.sawingCosts }}</td>
								<PriceStructureComponent :price="pricesIncVat.sawing" :isEx="false" :showSpinner="shouldShowSpinner" />
								<PriceStructureComponent :price="pricesExVat.sawing" :isEx="true" :showSpinner="shouldShowSpinner" />
							</tr>
							<tr v-if="showMinimumFee">
								<td>{{ i18n.meta.minimumPriceFee }}</td>
								<PriceStructureComponent :price="pricesIncVat.minimumPriceFee" :isEx="false" :showSpinner="shouldShowSpinner" />
								<PriceStructureComponent :price="pricesExVat.minimumPriceFee" :isEx="true" :showSpinner="shouldShowSpinner" />
							</tr>
							<tr v-if="showBulkPrice && pricesIncVat.bulkDiscount > 0">
								<td>{{ i18n.meta.bulkDiscount }}</td>
								<PriceStructureComponent :price="-1 * pricesIncVat.bulkDiscount" :isEx="false" :showSpinner="shouldShowSpinner" />
								<PriceStructureComponent :price="-1 * pricesExVat.bulkDiscount" :isEx="true" :showSpinner="shouldShowSpinner" />
							</tr>
							<tr v-if="showComplexStructure" class="measurements-selector__pricing-table__addition"><td></td><td></td><td></td></tr>
							<tr v-if="showComplexStructure">
								<td>{{ i18n.meta.unitPrice }}</td>
								<PriceStructureComponent :price="pricesIncVat.bulkUnitPrice" :isEx="false" :showSpinner="shouldShowSpinner" />
								<PriceStructureComponent :price="pricesExVat.bulkUnitPrice" :isEx="true" :showSpinner="shouldShowSpinner" />
							</tr>
							<tr v-if="showComplexStructure">
								<td></td>
								<td>x {{ quantity }}</td>
								<td>x {{ quantity }}</td>
							</tr>
							<tr>
								<td>{{ i18n.meta.shapeFee }}</td>
								<PriceStructureComponent :price="pricesIncVat.fee" :isEx="false" :showSpinner="shouldShowSpinner" />
								<PriceStructureComponent :price="pricesExVat.fee" :isEx="true" :showSpinner="shouldShowSpinner" />
							</tr>
							<tr v-if="showComplexStructure" class="measurements-selector__pricing-table__addition"><td></td><td></td><td></td></tr>
							<tr v-if="showComplexStructure">
								<td>{{ i18n.meta.total }}</td>
								<PriceStructureComponent :price="totalPriceIncVat" :isEx="false" :showSpinner="shouldShowSpinner" />
								<PriceStructureComponent :price="totalPriceExVat" :isEx="true" :showSpinner="shouldShowSpinner" />
							</tr>
						</tbody>
					</table>
					<table
						v-else
						class="measurements-selector__pricing-table"
					>
						<tbody>
							<tr>
								<td class="measurements-selector__pricing-section">
									<strong>{{ translatedShapeName }}</strong>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</measurements-height-transition>
		</div>
		<Teleport to="#measurements-selector-availability">
			<div class="measurements-selector__availability" v-if="availability" v-html="availability"></div>
		</Teleport>
		<BulkPriceTable
			:price="originalPriceExVat"
			:shape="shape"
			:primitives="primitives"
			v-on:discounted-price="updateDiscountedPrice"
			v-on:quantity="updateQuantity"
		/>
	</div>
</template>

<script lang="ts">
import { roundPrice } from "./../functions/prices";
import BulkPriceTable from "./BulkPriceTable.vue";
import PriceStructureComponent from "./PriceStructureComponent.vue";
import { mapGetters, mapMutations } from "vuex";
import mapValues from "lodash/mapValues";
import FormattedPrice from "./FormattedPrice.vue";
import { PriceCalculator } from "@/Models/PriceCalculator";
import {formatForInputField, formatToLocaleString, toNumber} from "../functions/numbers";
import ShareButton from "@/components/ShareButton.vue";
import ShareModal from "@/components/ShareModal.vue";
import StoreButton from "@/components/StoreButton.vue";
import StoreModal from "@/components/StoreModal.vue";
import MeasurementsHeightTransition from '@/components/UI/MeasurementsHeightTransition.vue';
import ChevronRightIcon from "./icons/ChevronRightIcon.vue";

export default {
	props: {
		isShapeComplete: Boolean,
		availability: String,
		shape: String,
		edgeFinish: String,
		primitives: Object,
		settings: Object,
		currency: String,
		locale: String,
		dxfDefinition: Object,
	},
	data: function() {
		return {
			discountedPriceExVat: null,
			showPriceStructure: false,
			priceStructureToggleUsed: false,
			quantity: 1,
		};
	},
	computed: {
		showMinimumFee() {
			return this.minimumPrice !== 0 &&
				this.pricesExVat.totalWithoutMinimum !== 0 &&
				this.minimumPrice > this.pricesExVat.totalWithoutMinimum;
		},
		minimumPrice: function() {
			return Math.max(
				this.settings.minimumPrice,
				this.getPricingGroup.get_minimum_selling_price( this.shape )
			);
		},
		calculator: function() {
			return new PriceCalculator(
				this.settings.pricePerM2ExcludingTax,
				this.minimumPrice,
				this.getPricingGroup.get_sawing_selling_price_per_m( this.activeEdgeFinish, this.shape )
			);
		},
		i18n: function() { return this.$i18n; },
		kpsSettings: function() { return this.$kpsSettings; },
		showPrices: function() {
			if ( this.shape === '' ) {
				return false;
			}

			return this.isRetrievingProperties || this.isShapeComplete || this.isRetrievingBulkPricing;
		},
		showComplexStructure() {
			return this.quantity > 1;
		},
		showBulkPrice() {
			const FIRST_BULK_THRESHOLD = 50;

			return (
				this.originalPriceExVat !== this.discountedPriceExVat && ! this.isRetrievingBulkPricing
			) || (
				this.originalPriceExVat !== this.discountedPriceExVat && this.isRetrievingBulkPricing && this.quantity >= FIRST_BULK_THRESHOLD
			);
		},
		shouldShowSpinner() {
			return ( this.usePriceFromServer && this.isRetrievingProperties ) || this.isRetrievingBulkPricing;
		},
		translatedShapeName: function() {
			return this.i18n.shapeNames[ this.shape ] ?? '';
		},
		priceExVat: function() {
			return null === this.discountedPriceExVat ? this.originalPriceExVat : this.discountedPriceExVat;
		},
		originalPriceExVat: function() {
			if ( ! this.isShapeComplete ) {
				return 0;
			}

			if ( this.usePriceFromServer ) {
				return this.dxfDefinition?.price ?? 0;
			}

			if ( this.isDefaultShape ) {
				return window.measurementSelectorVariables.settings.default_price_ex_tax;
			}

			return this.calculator.calculate( this.shapeObject );
		},
		isDefaultShape() {
			if (this.shape !== 'rectangle') {
				return false;
			}

			if (this.quantity > 1) {
				return false;
			}

			// Height is not default
			if (toNumber(this.baseShapeInstance.getDefaultDimensions().length) !== this.shapeObject.get_required_material().get_length().in_cm()) {
				return false;
			}

			// Width is not default
			if (toNumber(this.baseShapeInstance.getDefaultDimensions().width) !== this.shapeObject.get_required_material().get_width().in_cm()) {
				return false;
			}

			// Edge finish is not default
			if (this.activeEdgeFinish !== this.getPricingGroup.get_shape_settings('rectangle').sawing_method) {
				return false;
			}

			return true;
		},
		shapeFee() {
			return this.shape === 'rectangle' ? 0 : this.getPricingGroup.get_selling_starting_price();
		},
		pricesExVatBase: function() {
			if ( ! this.isShapeComplete ) {
				return {
					material: 0,
					sawing: 0,
					fee: 0,
					minimumPrice: this.minimumPrice,
					totalWithoutMinimum: 0
				};
			}

			if ( this.usePriceFromServer ) {
				const { material, sawing } = this.dxfDefinition?.prices ?? { material: 0, sawing: 0 };
				return {
					material,
					sawing,
					fee: this.shapeFee,
					minimumPrice: this.minimumPrice,
					totalWithoutMinimum: material + sawing,
				};
			}

			const material = this.calculator.calculate_material_price( this.shapeObject );
			const sawing   = this.calculator.calculate_sawing_price( this.shapeObject );

			return {
				material: material,
				sawing: sawing,
				fee: this.shapeFee,
				minimumPrice: this.minimumPrice,
				totalWithoutMinimum: material + sawing,
			};
		},
		pricesExVat() {
			const prices = Object.assign({}, this.pricesExVatBase);

			prices.unitPrice = Math.max( prices.minimumPrice, prices.totalWithoutMinimum );
			prices.bulkUnitPrice = this.discountedPriceExVat;

			prices.minimumPriceFee = 0;
			if ( prices.totalWithoutMinimum < prices.minimumPrice ) {
				prices.minimumPriceFee = prices.minimumPrice - prices.totalWithoutMinimum;
			}

			prices.bulkDiscount = 0;
			if ( prices.bulkUnitPrice !== null && prices.bulkUnitPrice < prices.unitPrice ) {
				prices.bulkDiscount = prices.unitPrice - prices.bulkUnitPrice;
			}

			return prices;
		},
		pricesIncVat() {
			return mapValues( this.pricesExVat, ( price ) => {
				return price * this.settings.vatRate;
			} );
		},
		priceIncVat() {
			if ( this.isDefaultShape ) {

				// For the default shape we use the price from the server, including vat.
				return window.measurementSelectorVariables.settings.default_price;
			}

			// This calculation is only necessary because of the minimum price.
			// In wooCommerce there is a correction of rounding in the vat calculation to fix an issue in Correct. We need to reproduce this here.
			return roundPrice( roundPrice(this.priceExVat) * this.settings.vatRate );
		},
		totalPriceExVat() {
			const price = roundPrice( this.priceExVat ) * this.quantity + this.shapeFee;
			this.updatePriceExcVat( price );
			return price;
		},
		totalPriceIncVat() {
			if ( this.isDefaultShape ) {

				// For the default shape we use the price from the server, including vat.
				const price = window.measurementSelectorVariables.settings.default_price;
				this.updatePriceIncVat( price );
				return price;
			}

			const vat = roundPrice( this.priceExVat * this.quantity ) * ( this.settings.vatRate - 1 );
			const exVat = this.priceExVat * this.quantity;
			const price = roundPrice( exVat + vat ) + ( this.shapeFee * this.settings.vatRate );
			this.updatePriceIncVat( price );
			return price;
		},
		...mapGetters( [
			'usePriceFromServer',
			'isRetrievingProperties',
			'isRetrievingBulkPricing',
			'activeEdgeFinish',
			'getPricingGroup',
			'shapeObject',
			'baseShapeInstance',
		] ),
	},
	methods: {
		formatForInputField,
		formatToLocaleString,
		updateDiscountedPrice( discountedPrice ) {
			if ( 0 === discountedPrice ) {
				return;
			}

			this.discountedPriceExVat = discountedPrice;
		},
		updateQuantity( quantity ) {
			this.quantity = quantity;
		},
		addDataLayerEvent() {
			if ( ! this.priceStructureToggleUsed ) {
				window.dataLayer.push( {
					event: 'price_structure_toggle',
					eventCategory: 'Price structure toggle',
					eventAction: document.title,
				} );

				this.priceStructureToggleUsed = true;
			}
		},
		...mapMutations( [
			'updatePriceExcVat',
			'updatePriceIncVat',
		] ),
	},
	components: {
		FormattedPrice,
		BulkPriceTable,
		PriceStructureComponent,
		ShareModal,
		ShareButton,
		StoreModal,
		StoreButton,
		MeasurementsHeightTransition,
		ChevronRightIcon,
	},
};
</script>
